/**
 * Schedule exporters — render the AI-generated weekly schedule as a
 * professional matrix (staff rows × day columns, work hours in each
 * cell) and export to PDF, Excel, or Word.
 *
 * Used by `DepartmentScheduleDialog.jsx` for every department schedule
 * (including per-restaurant F&B schedules). All three formats share the
 * same `buildMatrix(...)` so what's printed/exported is identical to
 * what's shown on screen.
 */
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function addDays(iso, n) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function fmtCellHours(shiftList) {
  // Multiple shifts on the same day are joined with " + ".
  // Empty list renders as the OFF marker (caller styles it grey).
  if (!shiftList || shiftList.length === 0) return '';
  return shiftList
    .map((s) => `${(s.start_time || '').trim()}–${(s.end_time || '').trim()}`.replace(/^–$/, ''))
    .filter(Boolean)
    .join(' + ');
}

function fmtTotalHours(shiftList) {
  // Sum HH:MM ranges into a decimal hour total per staff member.
  if (!shiftList || shiftList.length === 0) return 0;
  let total = 0;
  for (const s of shiftList) {
    const a = parseTime(s.start_time);
    const b = parseTime(s.end_time);
    if (a == null || b == null) continue;
    let diff = b - a;
    if (diff < 0) diff += 24; // overnight shift
    total += diff;
  }
  return Math.round(total * 100) / 100;
}

function parseTime(t) {
  if (!t) return null;
  const m = String(t).trim().match(/^(\d{1,2}):?(\d{0,2})/);
  if (!m) return null;
  const hh = parseInt(m[1], 10);
  const mm = parseInt(m[2] || '0', 10);
  if (Number.isNaN(hh)) return null;
  return hh + (mm || 0) / 60;
}

/**
 * Build a {staff, days, matrix, totals} structure from the raw AI shifts.
 * The roster (team) is the source of truth for row order so admins always
 * see every staff member, even those the AI assigned zero shifts to.
 */
export function buildScheduleMatrix({ schedule, team, weekStart }) {
  const shifts = schedule?.shifts || [];
  const roster = (team || []).filter((m) => (m.name || '').trim());

  // Stable staff order — start from saved roster, append any AI-only names.
  const seen = new Set();
  const rows = [];
  roster.forEach((m) => {
    if (!seen.has(m.name)) {
      seen.add(m.name);
      rows.push({ name: m.name, position: m.position || '' });
    }
  });
  shifts.forEach((s) => {
    const key = s.staff_name || s.staff_initials || '';
    if (key && !seen.has(key)) {
      seen.add(key);
      rows.push({ name: key, position: s.position || '' });
    }
  });

  // Day headers with date
  const dayHeaders = DAYS.map((d, i) => ({ label: d, date: addDays(weekStart, i) }));

  // Matrix: matrix[staffName][day] = [shifts...]
  const matrix = {};
  rows.forEach((r) => {
    matrix[r.name] = {};
    DAYS.forEach((d) => { matrix[r.name][d] = []; });
  });
  shifts.forEach((s) => {
    const key = s.staff_name || s.staff_initials || '';
    if (!key) return;
    if (!matrix[key]) {
      matrix[key] = {};
      DAYS.forEach((d) => { matrix[key][d] = []; });
    }
    if (!matrix[key][s.day]) matrix[key][s.day] = [];
    matrix[key][s.day].push(s);
  });

  // Per-staff weekly total
  const totals = {};
  rows.forEach((r) => {
    totals[r.name] = DAYS.reduce((sum, d) => sum + fmtTotalHours(matrix[r.name][d]), 0);
    totals[r.name] = Math.round(totals[r.name] * 100) / 100;
  });

  return { rows, dayHeaders, matrix, totals };
}

const todayStr = () => new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

/* ─────────────────────────  Excel  ───────────────────────── */

export async function exportScheduleExcel({ schedule, team, weekStart, deptLabel, restaurant, docName }) {
  const { rows, dayHeaders, matrix, totals } = buildScheduleMatrix({ schedule, team, weekStart });

  const wb = new ExcelJS.Workbook();
  wb.creator = 'Dreams Rose Hall · Schedule';
  wb.created = new Date();
  const ws = wb.addWorksheet('Schedule', {
    pageSetup: {
      paperSize: 9,
      orientation: 'landscape',
      fitToPage: true, fitToWidth: 1, fitToHeight: 0,
      margins: { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 },
    },
  });

  // Columns: Staff | Position | 7 days | Weekly total
  ws.columns = [
    { width: 22 },  // Staff
    { width: 18 },  // Position
    ...dayHeaders.map(() => ({ width: 14 })),
    { width: 12 },  // Total
  ];
  const totalCols = ws.columns.length;
  const lastColLetter = String.fromCharCode(64 + totalCols);

  // Title
  ws.getRow(1).height = 28;
  ws.mergeCells(`A1:${lastColLetter}1`);
  const t1 = ws.getCell('A1');
  t1.value = docName || `${restaurant?.name ? restaurant.name + ' · ' : ''}${deptLabel || 'Department'} Schedule`;
  t1.font = { name: 'Calibri', size: 18, bold: true, color: { argb: 'FF003DA5' } };
  t1.alignment = { vertical: 'middle', horizontal: 'center' };

  ws.getRow(2).height = 18;
  ws.mergeCells(`A2:${lastColLetter}2`);
  const t2 = ws.getCell('A2');
  t2.value = `Week of ${weekStart}  ·  Generated ${todayStr()}`;
  t2.font = { name: 'Calibri', size: 10, italic: true, color: { argb: 'FF4A5D5D' } };
  t2.alignment = { vertical: 'middle', horizontal: 'center' };

  // Header row (row 3)
  const headerRow = ws.getRow(3);
  headerRow.height = 24;
  const headerLabels = ['Staff', 'Position', ...dayHeaders.map((d) => `${d.label}\n${d.date.slice(5)}`), 'Total hrs'];
  headerLabels.forEach((label, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = label;
    cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF003DA5' } };
    cell.border = { top: { style: 'thin', color: { argb: 'FFD9E3F1' } }, bottom: { style: 'thin', color: { argb: 'FFD9E3F1' } } };
  });

  // Data rows
  rows.forEach((r, idx) => {
    const row = ws.getRow(4 + idx);
    row.height = 28;
    const altFill = idx % 2 === 1 ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF4F8FD' } } : null;
    const cName = row.getCell(1);
    cName.value = r.name;
    cName.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF1A2E2E' } };
    cName.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    if (altFill) cName.fill = altFill;

    const cPos = row.getCell(2);
    cPos.value = r.position || '';
    cPos.font = { name: 'Calibri', size: 10, italic: true, color: { argb: 'FF4A5D5D' } };
    cPos.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    if (altFill) cPos.fill = altFill;

    dayHeaders.forEach((dh, ci) => {
      const cell = row.getCell(3 + ci);
      const txt = fmtCellHours(matrix[r.name][dh.label]);
      cell.value = txt || 'OFF';
      cell.font = { name: 'Calibri', size: 10, color: { argb: txt ? 'FF1A2E2E' : 'FFB0B5BD' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      if (altFill && txt) cell.fill = altFill;
      if (!txt) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAFAFA' } };
    });

    const cTotal = row.getCell(totalCols);
    cTotal.value = totals[r.name] || 0;
    cTotal.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF003DA5' } };
    cTotal.alignment = { vertical: 'middle', horizontal: 'center' };
    cTotal.numFmt = '0.##" h"';
    if (altFill) cTotal.fill = altFill;
  });

  // Borders on the data block
  const lastDataRow = 3 + rows.length;
  for (let r = 3; r <= lastDataRow; r++) {
    for (let c = 1; c <= totalCols; c++) {
      const cell = ws.getRow(r).getCell(c);
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFE2DBC8' } },
        bottom: { style: 'thin', color: { argb: 'FFE2DBC8' } },
        left: { style: 'thin', color: { argb: 'FFE2DBC8' } },
        right: { style: 'thin', color: { argb: 'FFE2DBC8' } },
      };
    }
  }

  const filename = buildFilename(deptLabel, restaurant, weekStart, 'xlsx', docName);
  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
}

/* ─────────────────────────  PDF  ───────────────────────── */

export async function exportSchedulePdf({ schedule, team, weekStart, deptLabel, restaurant, docName }) {
  const { rows, dayHeaders, matrix, totals } = buildScheduleMatrix({ schedule, team, weekStart });
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();

  // Title band
  doc.setFillColor(0, 61, 165);
  doc.rect(0, 0, pageWidth, 16, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  const title = docName || `${restaurant?.name ? restaurant.name + ' · ' : ''}${deptLabel || 'Department'} Schedule`;
  doc.text(title, pageWidth / 2, 10, { align: 'center' });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Week of ${weekStart} · Generated ${todayStr()}`, pageWidth / 2, 14.5, { align: 'center' });

  // Table
  const head = [[
    'Staff', 'Position',
    ...dayHeaders.map((d) => `${d.label}\n${d.date.slice(5)}`),
    'Total hrs',
  ]];
  const body = rows.map((r) => [
    r.name,
    r.position || '',
    ...dayHeaders.map((dh) => fmtCellHours(matrix[r.name][dh.label]) || 'OFF'),
    `${totals[r.name] || 0} h`,
  ]);

  autoTable(doc, {
    head, body,
    startY: 20,
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 2, valign: 'middle', halign: 'center', textColor: [26, 46, 46] },
    headStyles: { fillColor: [0, 61, 165], textColor: 255, fontStyle: 'bold', halign: 'center', valign: 'middle' },
    bodyStyles: { lineColor: [226, 219, 200], lineWidth: 0.2 },
    alternateRowStyles: { fillColor: [244, 248, 253] },
    columnStyles: {
      0: { halign: 'left', fontStyle: 'bold', cellWidth: 32 },
      1: { halign: 'left', textColor: [74, 93, 93], cellWidth: 28 },
      [head[0].length - 1]: { fontStyle: 'bold', textColor: [0, 61, 165], cellWidth: 20 },
    },
    didParseCell: (data) => {
      // Grey-out OFF cells
      if (data.section === 'body' && data.column.index >= 2 && data.column.index <= 8 && data.cell.raw === 'OFF') {
        data.cell.styles.textColor = [176, 181, 189];
        data.cell.styles.fillColor = [250, 250, 250];
        data.cell.styles.fontStyle = 'italic';
      }
    },
  });

  // Footer notes (coverage + recommendations) — wrap text within page width
  let cursorY = doc.lastAutoTable.finalY + 6;
  const wrapAndPrint = (header, items) => {
    if (!items?.length) return;
    if (cursorY > 190) { doc.addPage(); cursorY = 20; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(0, 61, 165);
    doc.text(header, 10, cursorY); cursorY += 5;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(26, 46, 46);
    items.forEach((n) => {
      const lines = doc.splitTextToSize(`• ${n}`, pageWidth - 20);
      lines.forEach((ln) => {
        if (cursorY > 195) { doc.addPage(); cursorY = 20; }
        doc.text(ln, 10, cursorY); cursorY += 4;
      });
    });
    cursorY += 2;
  };
  wrapAndPrint('Coverage notes', schedule.coverage_notes || []);
  wrapAndPrint('Recommendations', schedule.recommendations || []);

  doc.save(buildFilename(deptLabel, restaurant, weekStart, 'pdf', docName));
}

/* ─────────────────────────  Word  ───────────────────────── */

export function exportScheduleWord({ schedule, team, weekStart, deptLabel, restaurant, docName }) {
  const { rows, dayHeaders, matrix, totals } = buildScheduleMatrix({ schedule, team, weekStart });
  const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const headerCells = ['Staff', 'Position', ...dayHeaders.map((d) => `${d.label}<br/><span style="font-size:8px;font-weight:400">${d.date.slice(5)}</span>`), 'Total hrs']
    .map((h) => `<th>${h}</th>`).join('');

  const bodyRows = rows.map((r, idx) => {
    const cells = [
      `<td style="font-weight:700">${esc(r.name)}</td>`,
      `<td style="font-style:italic;color:#4A5D5D">${esc(r.position || '')}</td>`,
      ...dayHeaders.map((dh) => {
        const txt = fmtCellHours(matrix[r.name][dh.label]);
        return txt
          ? `<td style="text-align:center">${esc(txt)}</td>`
          : `<td style="text-align:center;color:#B0B5BD;background:#FAFAFA;font-style:italic">OFF</td>`;
      }),
      `<td style="text-align:center;font-weight:700;color:#003DA5">${totals[r.name] || 0} h</td>`,
    ].join('');
    const bg = idx % 2 === 1 ? 'background:#F4F8FD;' : '';
    return `<tr style="${bg}">${cells}</tr>`;
  }).join('');

  const coverageHtml = (schedule.coverage_notes || []).length
    ? `<h3 style="color:#003DA5">Coverage notes</h3><ul>${(schedule.coverage_notes || []).map((n) => `<li>${esc(n)}</li>`).join('')}</ul>`
    : '';
  const recsHtml = (schedule.recommendations || []).length
    ? `<h3 style="color:#B45309">Recommendations</h3><ul>${(schedule.recommendations || []).map((n) => `<li>${esc(n)}</li>`).join('')}</ul>`
    : '';

  const titleText = docName ? esc(docName) : `${restaurant?.name ? esc(restaurant.name) + ' · ' : ''}${esc(deptLabel || 'Department')} Schedule`;

  const html = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
    <head><meta charset='utf-8'><title>${titleText}</title>
    <!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>90</w:Zoom></w:WordDocument></xml><![endif]-->
    <style>
      @page Section1 { size: 29.7cm 21cm; mso-page-orientation: landscape; margin: 1.2cm 1cm; }
      div.Section1 { page: Section1; }
      body { font-family: Calibri, Arial, sans-serif; font-size: 10px; color: #1A2E2E; }
      h1 { color: #003DA5; margin: 0 0 4px 0; font-size: 18px; }
      h3 { color: #003DA5; font-size: 12px; margin: 10px 0 4px 0; }
      .meta { color: #4A5D5D; font-size: 10px; font-style: italic; margin-bottom: 10px; }
      table { width: 100%; border-collapse: collapse; table-layout: fixed; }
      th, td { border: 1px solid #E2DBC8; padding: 5px 4px; vertical-align: middle; word-wrap: break-word; overflow-wrap: anywhere; }
      thead tr { background: #003DA5; color: white; }
      th { text-align: center; font-size: 11px; }
      td { font-size: 10px; }
      ul { padding-left: 18px; margin: 4px 0; }
      li { font-size: 10px; }
    </style></head>
    <body><div class='Section1'>
      <h1>${titleText}</h1>
      <p class='meta'>Week of ${esc(weekStart)} · Generated ${esc(todayStr())}</p>
      <table>
        <thead><tr>${headerCells}</tr></thead>
        <tbody>${bodyRows}</tbody>
      </table>
      ${coverageHtml}${recsHtml}
    </div></body></html>`;

  const blob = new Blob(['\ufeff' + html], { type: 'application/msword' });
  saveAs(blob, buildFilename(deptLabel, restaurant, weekStart, 'doc', docName));
}

/* ─────────────────────────  Print HTML (matrix)  ───────────────────────── */

/** Generate the matrix HTML used by `printHtml()` so on-screen print matches
 *  the exported documents. Caller wraps this with the universal print CSS. */
export function buildScheduleMatrixHtml({ schedule, team, weekStart, deptLabel, restaurant, logoUrl, accent, docName }) {
  const { rows, dayHeaders, matrix, totals } = buildScheduleMatrix({ schedule, team, weekStart });
  const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const a = accent || '#003DA5';

  const head = `<tr style="background:${esc(a)};color:white">
    <th style="text-align:left;padding:5px 6px">Staff</th>
    <th style="text-align:left;padding:5px 6px">Position</th>
    ${dayHeaders.map((d) => `<th style="text-align:center;padding:5px 4px">${esc(d.label)}<br/><span style="font-size:8px;font-weight:400;opacity:.85">${esc(d.date.slice(5))}</span></th>`).join('')}
    <th style="text-align:center;padding:5px 4px">Total</th>
  </tr>`;

  const body = rows.map((r, idx) => {
    const bg = idx % 2 ? 'background:#F4F8FD' : '';
    const dayCells = dayHeaders.map((dh) => {
      const txt = fmtCellHours(matrix[r.name][dh.label]);
      return txt
        ? `<td style="text-align:center;${bg}">${esc(txt)}</td>`
        : `<td style="text-align:center;color:#B0B5BD;background:#FAFAFA;font-style:italic">OFF</td>`;
    }).join('');
    return `<tr><td style="font-weight:700;${bg}">${esc(r.name)}</td>
      <td style="font-style:italic;color:#4A5D5D;${bg}">${esc(r.position || '')}</td>
      ${dayCells}
      <td style="text-align:center;font-weight:700;color:${esc(a)};${bg}">${totals[r.name] || 0} h</td></tr>`;
  }).join('');

  const titleText = docName ? esc(docName) : `${restaurant?.name ? esc(restaurant.name) + ' · ' : ''}${esc(deptLabel || 'Department')} Schedule`;
  const coverage = (schedule.coverage_notes || []).length
    ? `<h2 style="color:${esc(a)}">Coverage notes</h2><ul>${(schedule.coverage_notes || []).map((n) => `<li>${esc(n)}</li>`).join('')}</ul>` : '';
  const recs = (schedule.recommendations || []).length
    ? `<h2 style="color:#B45309">Recommendations</h2><ul>${(schedule.recommendations || []).map((n) => `<li>${esc(n)}</li>`).join('')}</ul>` : '';

  return `
    ${logoUrl ? `<img src="${esc(logoUrl)}" style="height:24mm;display:block;margin:0 auto 4mm"/>` : ''}
    <p class="kicker" style="color:${esc(a)}">DREAMS ROSE HALL · ${esc((deptLabel || '').toUpperCase())} SCHEDULE</p>
    <h1 style="color:${esc(a)}">${titleText}</h1>
    <p class="meta">Week of ${esc(weekStart)} · Generated ${esc(todayStr())}</p>
    <table style="font-size:10px"><thead>${head}</thead><tbody>${body}</tbody></table>
    ${coverage}${recs}`;
}

function buildFilename(deptLabel, restaurant, weekStart, ext, docName) {
  const safe = (s) => String(s || '').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-|-$/g, '');
  // Honor a user-supplied document name if present — keep it readable in
  // the filename rather than recomputing department / restaurant tokens.
  if ((docName || '').trim()) {
    const base = safe(docName) || 'schedule';
    return `${base}.${ext}`;
  }
  const restName = safe(restaurant?.name);
  const deptSafe = safe(deptLabel || 'schedule');
  // Dedup: if the restaurant name is already inside the dept label
  // (e.g. "Tree Palms Restaurant (Restaurant)" already starts with the
  // outlet name), drop the standalone restaurant token to avoid
  // "Tree-Palms-Restaurant-Tree-Palms-Restaurant-…".
  const labelLc = deptSafe.toLowerCase();
  const restLc = restName.toLowerCase();
  const restPart = restName && !labelLc.startsWith(restLc) ? restName : '';
  const parts = [restPart, deptSafe, safe(weekStart)].filter(Boolean);
  return `${parts.join('-')}.${ext}`;
}
